import { AggregationFn, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DateTimeService, ISOFormat, LocalizedCurrency, LocalizedDate, LocalizedPercentage, lt } from '@mews/framework';
import { LedgerActivityReportAccountType, LedgerActivityReportItem } from '@mews-commander/data-access';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { EnterpriseLink, TruncatedText } from '@mews-commander/ui';
import { sumTotalValues } from '../utils';
import { LedgerActivityReportItemLink } from '../components/LedgerActivityReportItemLink';
import { LedgerActivityReportBillLink } from '../components/LedgerActivityBillLink';

interface LedgerActivityReportTableMappingProps {
    ledgerTotal: number;
}

const TEXT_LENGTH = 35;

const sumFinancialValues: AggregationFn<LedgerActivityReportItem> = (columnId, _leafRows, childRows) => {
    return sumTotalValues(
        childRows.map((row) => row.original),
        columnId as keyof LedgerActivityReportItem
    );
};

const columnHelper = createColumnHelper<LedgerActivityReportItem>();

const LedgerActivityDate = ({ date }: { date?: string | null }) => {
    if (!date) {
        return null;
    }

    const dateToDateTime = DateTimeService.fromString(date, ISOFormat.Date);

    return <LocalizedDate date={dateToDateTime} />;
};

export const useLedgerActivityTableMapping = ({ ledgerTotal }: LedgerActivityReportTableMappingProps) => {
    const { defaultCurrencyCode } = useCurrentEnterprise();

    return useMemo(
        () => [
            columnHelper.accessor('accountingCategory', {}),
            columnHelper.accessor('id', {
                header: () => lt('AccountingCategory'),
                enableSorting: true,
                cell: (props) => <LedgerActivityReportItemLink ledgerItem={props.row.original} />,
                meta: {
                    width: '15%',
                },
                footer: () => (
                    <Typography as="span" textStyle="bodyMediumStrong">
                        {lt('Total')}
                    </Typography>
                ),
            }),
            columnHelper.accessor('bill.id', {
                header: () => lt('Bill'),
                enableSorting: true,
                cell: (props) => <LedgerActivityReportBillLink ledgerItem={props.row.original} />,
                meta: {
                    width: '15%',
                },
            }),
            columnHelper.accessor('owner.id', {
                header: () => lt('Owner'),
                enableSorting: true,
                cell: (info) => {
                    const { owner } = info.row.original;
                    if (!owner) {
                        return (
                            <Typography data-test-id="no-owner-name" textStyle="bodyMedium" as="span">
                                {UnicodeEntity.Dash}
                            </Typography>
                        );
                    }
                    return (
                        <EnterpriseLink
                            variant="basic"
                            to={
                                owner.type === LedgerActivityReportAccountType.Company
                                    ? 'Company/Dashboard/$id'
                                    : 'Customer/Dashboard/$id'
                            }
                            params={{ id: owner.id ?? '' }}
                        >
                            <TruncatedText text={owner.name ?? ''} maxLength={TEXT_LENGTH} />
                        </EnterpriseLink>
                    );
                },
                meta: {
                    width: '15%',
                },
            }),
            columnHelper.accessor('accounted', {
                header: () => lt('Accounted'),
                cell: (props) => <LedgerActivityDate date={props.getValue()} />,
                enableSorting: true,
                meta: {
                    width: '15%',
                },
            }),
            columnHelper.accessor('count', {
                header: () => lt('Count'),
                enableSorting: true,
                cell: (props) => (
                    <Typography textStyle="bodyMedium" as="span">
                        {props.getValue()}
                    </Typography>
                ),
                aggregatedCell: ({ getValue }) => (
                    <Typography textStyle="bodyMedium" as="span">
                        {getValue()}
                    </Typography>
                ),
                aggregationFn: 'count',
                meta: {
                    width: '10%',
                },
            }),
            columnHelper.accessor('taxRate', {
                header: () => lt('TaxRate'),
                enableSorting: true,
                cell: (props) => {
                    const { taxRate } = props.row.original;
                    return typeof taxRate === 'number' ? <LocalizedPercentage value={taxRate} /> : null;
                },
                aggregationFn: undefined,
                meta: {
                    width: '10%',
                },
            }),
            columnHelper.accessor('netBaseValue', {
                header: () => lt('AmountNet'),
                enableSorting: true,
                cell: (props) => {
                    const { netBaseValue } = props.row.original;
                    return typeof netBaseValue === 'number' ? (
                        <LocalizedCurrency currency={defaultCurrencyCode} value={netBaseValue} />
                    ) : null;
                },
                aggregationFn: sumFinancialValues,
                aggregatedCell: ({ getValue }) => {
                    const value = getValue();
                    return typeof value === 'number' ? (
                        <LocalizedCurrency currency={defaultCurrencyCode} value={value} />
                    ) : null;
                },
                meta: {
                    align: 'end',
                    width: '10%',
                },
            }),
            columnHelper.accessor('value', {
                header: () => lt('Amount'),
                enableSorting: true,
                cell: (props) => {
                    const { value } = props.row.original;
                    return typeof value === 'number' ? (
                        <LocalizedCurrency currency={defaultCurrencyCode} value={value} />
                    ) : null;
                },
                aggregationFn: sumFinancialValues,
                aggregatedCell: ({ getValue }) => {
                    const value = getValue();
                    return typeof value === 'number' ? (
                        <LocalizedCurrency currency={defaultCurrencyCode} value={value} />
                    ) : null;
                },

                footer: () =>
                    typeof ledgerTotal === 'number' ? (
                        <LocalizedCurrency currency={defaultCurrencyCode} value={ledgerTotal} />
                    ) : null,
                meta: {
                    align: 'end',
                },
            }),
        ],
        [defaultCurrencyCode, ledgerTotal]
    );
};
